import { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  Link as LinkIcon, ArrowClockwise, Plus, Copy, ArrowSquareOut, MagnifyingGlass, X,
} from '@phosphor-icons/react';
import { GeneratePaymentLinkDialog } from './GeneratePaymentLinkDialog';

const fmtINR = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const STATUS_OPTIONS = [
  { v: 'pending', l: 'Pending' },
  { v: 'link_generated', l: 'Link Generated' },
  { v: 'paid', l: 'Paid' },
  { v: 'partial_paid', l: 'Partially Paid' },
  { v: 'expired', l: 'Expired' },
  { v: 'cancelled', l: 'Cancelled' },
  { v: 'failed', l: 'Failed' },
];

const statusColor = (s) => {
  switch (s) {
    case 'paid': return 'bg-green-100 text-green-800';
    case 'partial_paid': return 'bg-amber-100 text-amber-800';
    case 'link_generated': return 'bg-blue-100 text-blue-800';
    case 'expired': return 'bg-zinc-200 text-zinc-700';
    case 'cancelled': return 'bg-zinc-200 text-zinc-700';
    case 'failed': return 'bg-red-100 text-red-800';
    default: return 'bg-orange-100 text-orange-800';
  }
};

const receiptTypeColor = (t) => {
  switch (t) {
    case 'advance': return 'bg-emerald-100 text-emerald-800';
    case 'adjustment': return 'bg-purple-100 text-purple-800';
    default: return 'bg-blue-100 text-blue-800';
  }
};

export function PaymentLinksTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingId, setRefreshingId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [filters, setFilters] = useState({
    status: '', customer_mobile: '', jo_number: '', quotation_number: '',
    date_from: '', date_to: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_mobile: true, limit: 500 };
      Object.entries(filters).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        params[k] = v;
      });
      const { data } = await api.get('/api/admin/payment-links', { params });
      setRows(data.payment_links || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payment links');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const refresh = async (row) => {
    if (row.source === 'mobile') {
      toast.info('Mobile app links are managed by the mobile backend');
      return;
    }
    const id = row._id || row.provider_link_id;
    if (!id) return;
    setRefreshingId(id);
    try {
      const { data } = await api.post(`/api/admin/payment-links/${encodeURIComponent(id)}/refresh`);
      toast.success(`Status: ${data.status?.toUpperCase() || 'unknown'}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    } finally { setRefreshingId(null); }
  };

  const copy = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  const clearFilter = () => setFilters({
    status: '', customer_mobile: '', jo_number: '', quotation_number: '',
    date_from: '', date_to: '',
  });

  const totals = rows.reduce((acc, r) => {
    acc.count += 1;
    acc.amount += Number(r.amount || 0);
    if (r.status === 'paid' || r.status === 'partial_paid') {
      acc.paidCount += 1;
      acc.paidAmount += Number(r.paid_amount || r.amount || 0);
    } else if (r.status === 'pending' || r.status === 'link_generated') {
      acc.openCount += 1;
      acc.openAmount += Number(r.amount || 0);
    }
    return acc;
  }, { count: 0, amount: 0, paidCount: 0, paidAmount: 0, openCount: 0, openAmount: 0 });

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard title="Total Links" value={totals.count} sub={fmtINR(totals.amount)} accent="text-zinc-900" />
        <MetricCard title="Paid" value={totals.paidCount} sub={fmtINR(totals.paidAmount)} accent="text-emerald-700" />
        <MetricCard title="Open (Pending / Sent)" value={totals.openCount} sub={fmtINR(totals.openAmount)} accent="text-blue-700" />
        <MetricCard title="Others (Failed / Expired)" value={totals.count - totals.paidCount - totals.openCount} accent="text-zinc-500" />
      </div>

      <Card className="border-zinc-300 rounded-sm">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <FField label="Status">
              <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="pl-filter-status"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {STATUS_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </FField>
            <FField label="Mobile">
              <Input value={filters.customer_mobile}
                onChange={(e) => setFilters({ ...filters, customer_mobile: e.target.value })}
                placeholder="10-digit"
                data-testid="pl-filter-mobile" />
            </FField>
            <FField label="JO Number">
              <Input value={filters.jo_number}
                onChange={(e) => setFilters({ ...filters, jo_number: e.target.value })}
                data-testid="pl-filter-jo" />
            </FField>
            <FField label="Quotation No">
              <Input value={filters.quotation_number}
                onChange={(e) => setFilters({ ...filters, quotation_number: e.target.value })}
                data-testid="pl-filter-quot" />
            </FField>
            <FField label="From">
              <Input type="date" value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
            </FField>
            <FField label="To">
              <Input type="date" value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
            </FField>
          </div>
          <div className="flex flex-wrap gap-2 justify-end items-center mt-4 pt-3 border-t border-zinc-100">
            <Button variant="outline" onClick={clearFilter}><X size={14} className="mr-1" /> Clear</Button>
            <Button variant="outline" onClick={load} data-testid="pl-reload"><ArrowClockwise size={14} /></Button>
            <Button onClick={() => setShowNew(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="pl-new">
              <Plus size={14} className="mr-1" /> New Payment Link
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-zinc-300 rounded-sm">
        <CardContent className="p-0 overflow-x-auto">
          <div className="p-3 text-xs text-zinc-500 border-b border-zinc-100">
            {rows.length} link{rows.length === 1 ? '' : 's'}
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="p-3 text-left">Created</th>
                <th className="p-3 text-left">Customer</th>
                <th className="p-3 text-left">Mobile</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">JO / Quot</th>
                <th className="p-3 text-right">Amount</th>
                <th className="p-3 text-right">Paid</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-left">Link</th>
                <th className="p-3 text-left">Receipt No</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="p-6 text-center text-zinc-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={11} className="p-6 text-center text-zinc-400">
                  <div className="flex flex-col items-center gap-2">
                    <MagnifyingGlass size={22} />
                    <div>No payment links yet. Click <span className="font-bold text-orange-600">New Payment Link</span> to generate one.</div>
                  </div>
                </td></tr>
              ) : rows.map((r) => {
                const key = r._id || r.provider_link_id;
                const type = r.receipt_type || (r.jo_number || r.quotation_number ? 'quotation' : 'advance');
                const linkUrl = r.link_url || r.short_url;
                return (
                  <tr key={key} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`pl-row-${key}`}>
                    <td className="p-3 text-xs text-zinc-600">
                      <div>{(r.created_at || '').slice(0, 10)}</div>
                      <div className="text-[10px] text-zinc-400">{(r.created_at || '').slice(11, 16)}</div>
                      {r.source === 'mobile' && <Badge className="mt-1 text-[9px] bg-blue-50 text-blue-700">MOBILE</Badge>}
                    </td>
                    <td className="p-3">{r.customer_name || '—'}</td>
                    <td className="p-3 font-mono text-xs text-zinc-700">{r.customer_mobile || '—'}</td>
                    <td className="p-3">
                      <Badge className={`text-[10px] ${receiptTypeColor(type)}`}>{type.toUpperCase()}</Badge>
                    </td>
                    <td className="p-3 font-mono text-xs">
                      {r.jo_number && <div>{r.jo_number}</div>}
                      {r.quotation_number && <div className="text-zinc-500">{r.quotation_number}</div>}
                      {!r.jo_number && !r.quotation_number && <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="p-3 text-right font-bold text-zinc-800">{fmtINR(r.amount)}</td>
                    <td className="p-3 text-right text-emerald-700 font-bold">{fmtINR(r.paid_amount)}</td>
                    <td className="p-3 text-center">
                      <Badge className={`text-[10px] ${statusColor(r.status)}`}>
                        {(r.status || 'pending').replace('_', ' ').toUpperCase()}
                      </Badge>
                    </td>
                    <td className="p-3">
                      {linkUrl ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => copy(linkUrl)}
                            className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-xs"
                            title="Copy link"
                            data-testid={`pl-copy-${key}`}>
                            <Copy size={12} /> Copy
                          </button>
                          <a href={linkUrl} target="_blank" rel="noreferrer"
                            className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-xs ml-1"
                            title="Open in new tab">
                            <ArrowSquareOut size={12} /> Open
                          </a>
                        </div>
                      ) : (
                        <span className="text-[10px] text-zinc-400">
                          {r.last_error ? `Error: ${String(r.last_error).slice(0, 30)}…` : '—'}
                        </span>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs text-orange-700">{r.receipt_number || '—'}</td>
                    <td className="p-3 text-center">
                      {r.source !== 'mobile' && (
                        <Button size="sm" variant="outline"
                          onClick={() => refresh(r)}
                          disabled={refreshingId === (r._id || r.provider_link_id)}
                          data-testid={`pl-refresh-${key}`}
                          className="text-xs h-7 px-2">
                          {refreshingId === (r._id || r.provider_link_id) ? (
                            <ArrowClockwise size={12} className="animate-spin" />
                          ) : (
                            <><ArrowClockwise size={12} className="mr-1" /> Refresh</>
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <GeneratePaymentLinkDialog
        open={showNew}
        onOpenChange={setShowNew}
        onCreated={() => load()}
      />
    </>
  );
}

function FField({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</Label>
      {children}
    </div>
  );
}

function MetricCard({ title, value, sub, accent }) {
  return (
    <Card className="border-zinc-300 rounded-sm">
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{title}</div>
        <div className={`text-lg font-bold mt-1 ${accent || 'text-zinc-800'}`}>{value}</div>
        {sub && <div className="text-[10px] text-zinc-400 mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}
